import React, { useCallback, useMemo } from 'react';

// DEPENDENCIES
import _ from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useFormikContext } from 'formik';
import * as yup from 'yup';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints';

// GLOBAL FUNCTIONS
import { makePath, doCallback } from 'functions';
import useGetDealerSettings from './hooks/useGetDealerSettings';

// GLOBAL COMPONENTS
import Form from 'core/form/Form';
import Page from 'parts/page/Page';
import Card from 'parts/card/Card';

// STYLES
import styles from './dealerSettings/dealerSetting.module.scss';

// MAIN COMPONENT
const DealerSettings = () => {
  const settings = useGetDealerSettings();

  if (_.isEmpty(settings)) return null;

  return (
    <Page.Update
      title="Dealer Settings"
      label="dealer settings"
      method="POST"
      endpoint={makePath(ENDPOINTS.admin.dealerSettings, `?dealer_setting_id=${settings.dealer_setting_id}`)}
      initialValues={{
        ...settings
      }}
    >
      <Card body={<FormBody />} />
    </Page.Update>
  );
};

const FormBody = compose(connect(({ lookups }) => ({ lookups })))(
  ({
    // REDUX STATE
    lookups
  }) => {
    // FORMIK CONTEXT
    const { values, setFieldValue } = useFormikContext() || {};
    const {
      homenet_setting_new, //
      homenet_setting_used,
      make_dealer_id,
      push_inventory_to_dims,
      dims_dealership_id
    } = values || {};

    // REDUX
    const totalMakes = useMemo(
      () => lookups['vehicleMakes'] || [],
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [lookups.length]
    );

    // CALLBACKS
    const onMakeChange = useCallback(
      e => {
        const { value, checked } = e.target;
        const make = totalMakes.find(m => String(m.vehicle_make_id) === String(value));
        let homeNetSettingNew = [...homenet_setting_new];
        if (!make) return;
        
        if (checked) {
          const newMake = {
            makeid: value,
            makename: make?.vehicle_make_name,
            new_used: 1,
            make_dealer_id: make_dealer_id,
            rooftop_name: '',
            dealer_homenet_setting_id: ''
          };
          homeNetSettingNew = [...homeNetSettingNew, newMake];
        } else {
          homeNetSettingNew = homeNetSettingNew.filter(h => String(h.makeid) !== String(value));
        }
        doCallback(setFieldValue, 'homenet_setting_new', homeNetSettingNew);
      },
      [homenet_setting_new, make_dealer_id, setFieldValue, totalMakes]
    );

    const DIMS_ID_validation = useMemo(() => {
      return yup.mixed().test(dims_dealership_id, `Must be no more than 9 characters.`, v => v === undefined || v.length <= 9);
    }, [dims_dealership_id]);

    return (
      <Form.Body className={styles.dealerSetting}>
        <Form.Control name="dealer_setting_id" hidden />
        <Form.Control name="make_dealer_id" hidden />
        <Form.Control name="created_by_user_id" hidden />
        <Form.Control name="updated_by_user_id" hidden />
        <Form.YesNo name="seperate_after_market_dept" label="Separate AM Dept" required />
        <Form.YesNo name="push_inventory_to_ttms" label="Push Inventory to TTMS" required />
        <Form.YesNo name="push_inventory_to_ecosteamobile" label="Push Inventory to Ecosteamobile" required />
        <Form.YesNo name="push_inventory_to_mapa" label="Push Inventory to MAPA" required />
        <Form.YesNo
          name="require_am_product_upload_for_delivery"
          label="Require AM Product Agreement Upload for Deal Delivery?"
          required
        />
        <Form.Control name="makes" hidden />
        <Form.Checklist
          className={styles.makes}
          name="make_ids"
          label="Makes"
          labelProps={{
            className: styles.checklistLabel
          }}
          lookup="vehicleMakes"
          optionKeys={{
            label: 'vehicle_make_name',
            value: 'vehicle_make_id'
          }}
          useCSV
          onChange={onMakeChange}
          useNone
          fullWidth
        />
        <Form.Control name="certificate_number" label="Dealer DMV Certificate #" />
        <Form.Control name="dealer_number" label="Dealer #" />
        <Form.YesNo name="push_inventory_to_dims" label="DIMS Interface Active" required />
        <Form.Control
          name="dims_dealership_id"
          label="DIMS Dealership ID"
          required={!!parseInt(push_inventory_to_dims)}
          validationSchema={DIMS_ID_validation}
        />
        <Form.YesNo name="homenet_ntn_integration" label="Homenet NTN Integration" required />
        <Form.Control name="homenet_ntn_integration_token" label="Homenet NTN Integration Token" />
        {!!homenet_setting_used && (
          <Form.Control
            name={`homenet_setting_used.rooftop_name`}
            label={`Homenet IMV Dealer Name for Used ${homenet_setting_used.makename}`}
          />
        )}
        {!!homenet_setting_new.length &&
          homenet_setting_new.map((v, i) => (
            <Form.Control
              key={`homenet_setting_new-${i}`}
              name={`homenet_setting_new.[${i}].rooftop_name`}
              label={`Homenet IMV Dealer Name for New ${v?.makename}`}
            />
          ))}
      </Form.Body>
    );
  }
);

// EXPORT
export default DealerSettings;
